import { NotificationTypes } from '../components/routes/Home/application/detail-view/tabs/type';
import { COST_SCOPE_REFS, CostScopeRef } from '../const/cost.const';
import { INPUT_REGEX, PRESENCE_VALUE, RULE_VALUE } from '../helpers';
import {
  CostResultKey,
  HistoryAction,
  HistoryEvent,
  IApplicationComponentRef,
  IApplicationScopeRef,
  IReferenceValue,
  NotificationType,
  PreviousValueCmpType,
  ProfileType,
  TypeMode,
} from '../types';
import { formatCostKey } from './format';
import { hasRcFormat } from './personalization.util';

export const truncateString = (str: string, num: number): string => {
  if (str.length === 0 || str.length <= num) {
    return str;
  }

  return str.slice(0, num) + '...';
};

export const formatPrice = (price: number, isCompact?: boolean): string => {
  return new Intl.NumberFormat(
    'fr-FR',
    isCompact
      ? {
          notation: 'compact',
          maximumFractionDigits: 1,
        }
      : {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }
  ).format(price);
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    notation: 'compact',
  }).format(value);
};

export const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

export function camelToLabel(value: string) {
  return (value?.trim() ?? '').replace(/[A-Z]/g, (char) => ` ${char.toLowerCase()}`);
}

export function keyToLabel(value: string) {
  return capitalizeFirstLetter(value.split('-').join(' '));
}

export const isNumeric = (str: string): boolean => str.match('[0-9]+[\\.]?[0-9]*') !== null;

export const displayName = (email: string): string => email.replace('-ext', '').split('@')[0].split('.').join(' ');

export const capitalizeEachFirstLetter = (name: string): string =>
  name ? name.replace(/(^\w|\s\w|-\w)/g, (str) => str.toUpperCase()) : name;

export const isUrl = (str: string): boolean =>
  str.match(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi) !==
  null;

export const formatPresenceValue = (value?: string | null): string => {
  switch (value?.toLowerCase()) {
    case undefined:
    case null:
    case 'n/d':
      return 'No data';
    case 'n/a':
      return 'Not applicable';
    case '0':
    case 'not used':
      return 'Not used';
    case '0.5':
      return 'Partially used';
    case 'used':
    case '1':
    default:
      return 'Used';
  }
};

export const formatSearchResult = (value: string[], filterName: string): string | undefined =>
  value.find((filter) => filter.split(':')[0] === filterName)?.split(':')[1];

export const getValuesAccordingToType = (type: string): IReferenceValue[] => {
  switch (type) {
    case 'rule':
      return RULE_VALUE;
    case 'presence':
      return PRESENCE_VALUE;
    default:
      return [];
  }
};

export const isTcoKey = (
  childrenId: string,
  level: string,
  scope: IApplicationScopeRef[],
  components: IApplicationComponentRef[]
): boolean => {
  if (level === 'scope') {
    const scopeRefFiltered = scope.find((scopeRef) => scopeRef.id === Number(childrenId));
    if (!scopeRefFiltered) return false;

    return scopeRefFiltered.key === 'cost';
  } else {
    const componentRefFiltered = components.find((componentRef) => componentRef.id === Number(childrenId));

    if (!componentRefFiltered) return false;
    const costScopeRefs = scope.filter((scopeRef) => COST_SCOPE_REFS.includes(scopeRef.key as CostScopeRef));
    const costComponent = costScopeRefs
      ? costScopeRefs.flatMap((scopeRef) => scopeRef.componentsRef).map((componentRef) => componentRef.key)
      : [];
    return costComponent.includes(componentRefFiltered.key);
  }
};

export const nameFilter = (
  hasSomeName: boolean
): ((value: string, index: number, self: readonly string[]) => boolean) => {
  return (value: string) => {
    // Environment is an alias of Name
    if (value === 'Name' || value === 'Environment') {
      return hasSomeName;
    }
    return true;
  };
};

export const generationFilter = (
  displayGeneration: boolean
): ((value: string, index: number, self: readonly string[]) => boolean) => {
  return (value) => {
    if (value === 'Generation') {
      return displayGeneration;
    }
    if (value === 'Name') {
      return !displayGeneration;
    }
    return true;
  };
};

export const environmentFilter = (
  isFinops: boolean,
  isMonitoring: boolean
): ((value: string, index: number, self: readonly string[]) => boolean) => {
  return (value) => {
    if (value === 'Environment') {
      return isFinops || isMonitoring;
    }
    if (value === 'Name') {
      return !isFinops && !isMonitoring;
    }
    return true;
  };
};

export const recommendationFilter = (
  hasRecommendation: boolean
): ((value: string, index: number, self: readonly string[]) => boolean) => {
  return (value: string) => {
    if (value === 'Recommendation') {
      return hasRecommendation;
    }
    return true;
  };
};

export const getIconMetadata = (previousValue: PreviousValueCmpType): string => {
  switch (previousValue) {
    case 'increase':
      return 'north_east';
    case 'decrease':
      return 'south_east';
    default:
      return '';
  }
};

export const formatCriticity = (criticity: string): string => {
  switch (criticity) {
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'high':
      return 'High';
    case 'very-high':
      return 'Very high';
    default:
      return 'medium';
  }
};

export const formatCostLabel = (costScopeRef: CostResultKey, type: string | undefined, stack: TypeMode) => {
  return costScopeRef.split('_').at(-1)?.startsWith('workforce')
    ? `${capitalizeEachFirstLetter(formatCostKey(costScopeRef)!)}${
        stack === TypeMode.PREV_YEAR ? ' - Previous year' : ''
      } ${type ? `(${type})` : ''}`
    : `${capitalizeEachFirstLetter(formatCostKey(costScopeRef)!)}${
        stack === TypeMode.PREV_YEAR ? ' - Previous year' : ''
      }`;
};

export const formatChasingAction = (action: string): string | undefined => {
  switch (action) {
    case 'component-status':
      return 'Certification chasing';
    case 'result':
      return 'Specific result chasing';
    case 'recommendation':
      return 'Recommendation chasing';
    case 'roadmap':
      return 'Roadmap outdated chasing';
    case 'application':
      return 'Specific chasing';
    default:
      return 'Undefined';
  }
};

export const formatRcSigle = (userRc: string, profileType: ProfileType | null) => {
  return userRc.endsWith('DIR') || hasRcFormat(profileType) ? userRc.substring(0, userRc.lastIndexOf('/')) : userRc;
};

export const formatEventColour = (event: HistoryEvent) => {
  const eventComputed: HistoryAction = event.split('.')[1] as HistoryAction;

  switch (eventComputed) {
    case 'creation':
      return 'success';
    case 'addition':
      return 'success';
    case 'update':
      return 'info';
    default:
      return 'danger';
  }
};

export const isInputClean = (value: string | undefined) => {
  return value !== undefined && INPUT_REGEX.test(value);
};

export const getRuleByResult = (groupedRules: string[]) => {
  const hasBadRules = groupedRules.some((rules) => rules === 'bad');
  const hasMediumRules = groupedRules.some((rules) => rules === 'medium');
  const hasGoodRules = groupedRules.some((rules) => rules === 'good' || rules === 'intermediate');

  return hasBadRules ? 'bad' : hasMediumRules ? 'medium' : hasGoodRules ? 'good' : null;
};

/**
 * Remove a key from a URL search string
 * @param search A URL search string, beginning with a ?.
 * @param key The key to remove
 * @returns Same URL without the "key"
 */
export const removeQueryParam = (search: string, key: string): string => {
  const re = new RegExp(`(&)?${key}=.*?(&|$)`);
  const replacedSearch = search.replace(re, '');
  return replacedSearch === '?' ? '' : replacedSearch;
};

/**
 * Remove multiple key from a URL search string
 * @param search A URL search string, beginning with a ?.
 * @param keys The keys to remove
 * @returns Same URL without the "key"
 */
export const removeQueryParams = (search: string, keys: Array<string>): string => {
  return keys.reduce(removeQueryParam, search);
};

export const pluralizeValue = (item: number, value: string): string => {
  return item + ` ${value}${item > 1 ? 's' : ''}`;
};

export const notificationText = (type: NotificationType): string => {
  switch (type) {
    case NotificationTypes.RECOMMENDATION:
      return 'a recommendation';
    case NotificationTypes.ROADMAP:
      return 'a roadmap';
    case NotificationTypes.ROADMAP_OUTDATED:
      return 'an outdated roadmap';
    case NotificationTypes.EXCEPTION:
      return 'an exception';
    case NotificationTypes.CLAIM:
      return 'a claim';
    default:
      return '';
  }
};

export const isCurrentView = (pathname: string, includedPath: string[]) => includedPath.includes(pathname);

export const computeStringForPopover = (str: string): string => {
  return str.replaceAll(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/\s]/gi, '');
};
